import React from "react";
import { useTheme } from "@/context/ThemeContext";
import { formatCurrency } from "@/modules/stochastic/features/SalesAndCustomerDashboard/utils/chart";

interface ChartDataItem {
  postalCode: string;
  [key: string]: string | number;
}

interface ColoredYearTooltipProps {
  active?: boolean;
  payload?: { payload: ChartDataItem }[];
  colors: Record<string, string>;
  labelKey?: keyof ChartDataItem;
  labelPrefix?: string;
  valueSuffix?: string;
}

const ChartTooltipWithColoredYears = ({
  active,
  payload,
  colors,
  labelKey = "postalCode",
  labelPrefix = "ZIP:",
  valueSuffix = "%",
}: ColoredYearTooltipProps) => {
  const { theme } = useTheme();

  if (!active || !payload?.length) return null;

  const data = payload[0].payload;

  const avgPercentage = data.avgPercentage;
  const avgSales = data.avgSales;

  const entries = Object.entries(data)
    .filter(
      ([key]) =>
        key !== labelKey && key !== "avgPercentage" && key !== "avgSales",
    )
    .sort(([a], [b]) => a.localeCompare(b));

  const styles = {
    backgroundColor: theme === "dark" ? "#1F2937" : "#FFFFFF",
    borderColor: theme === "dark" ? "#374151" : "#E5E7EB",
    color: theme === "dark" ? "#F3F4F6" : "#1F2937",
  };

  const formatValue = (value: string | number) => {
    // If valueSuffix is empty, format as currency
    if (valueSuffix === "" && typeof value === "number") {
      return formatCurrency(value);
    }
    // Otherwise, display value with suffix
    return `${value}${valueSuffix}`;
  };

  return (
    <div
      className="px-4 py-3 rounded-lg shadow-lg border text-sm"
      style={styles}
    >
      <div className="text-base font-semibold">
        {labelPrefix} {data[labelKey]}
      </div>
      <hr className="my-2" style={{ borderColor: styles.borderColor }} />
      <div className="space-y-1">
        {entries.map(([year, value]) => (
          <div className="flex justify-between gap-4" key={year}>
            <span
              className="font-semibold"
              style={{ color: colors[year] ?? styles.color }}
            >
              {year}:
            </span>
            <span
              className="font-semibold"
              style={{ color: colors[year] ?? styles.color }}
            >
              {formatValue(value)}
            </span>
          </div>
        ))}
      </div>
      {avgPercentage !== undefined && (
        <>
          <hr className="my-2" style={{ borderColor: styles.borderColor }} />
          <div className="flex justify-between gap-4 text-gray-900 dark:text-gray-100 font-semibold">
            <span>Average Percentage:</span>
            <span>{formatValue(avgPercentage)}</span>
          </div>
        </>
      )}
      {avgSales !== undefined && (
        <>
          <hr className="my-2" style={{ borderColor: styles.borderColor }} />
          <div className="flex justify-between gap-4 text-gray-900 dark:text-gray-100 font-semibold">
            <span>Average Sales:</span>
            <span>{formatCurrency(avgSales as number)}</span>
          </div>
        </>
      )}
    </div>
  );
};

export default ChartTooltipWithColoredYears;
